import { msg } from "@omegagrid/localize";
import { NodeSource, TreeAdapter, TreeOptions, TreeSource } from "@omegagrid/tree";
import rp from "@reporting/rp-app-host";
import { User } from "@reporting/rp-types/core";
import { AccessRole } from "@reporting/rp-types/users";

export class UserTreeAdapter extends TreeAdapter {

	private items: NodeSource[];
	public accounting: number;

	constructor(
		private includeRoles: boolean = true,
		private options?: Partial<TreeOptions>
	) { super() }

	async getTree(): Promise<TreeSource> {
		if (this.items) return this.items;
		// if includeRoles is true, fetch roles and add 'r' and 'u' prefixes to keys
		const roles = this.includeRoles ? await rp.ms('users').get<AccessRole[]>('accessRoles', { accounting: this.accounting }) : [];
		const users = await rp.ms('users').get<User[]>('groupUsers', { accounting: this.accounting });
		const saUsers = rp.userProvider.isSA() ? await rp.ms('users').get<User[]>('saUsers', { accounting: this.accounting }) : [];

		this.items = [];
		if (roles.length > 0) {
			if (users.length > 0) this.items.push({ k: 'roles', v: msg('Roles'), ch: false, l: 0, icon: 'users' });
			roles.forEach(role => this.items.push({ k: `r${role.id_access_role}`, v: role.role, l: users.length > 0 ? 1 : 0 }));
		}
		if (users.length > 0) {
			if (roles.length > 0) this.items.push({ k: 'users', v: msg('Users'), ch: false, l: 0, icon: 'user' });
			users.forEach(user => this.items.push({ k: `${this.includeRoles ? 'u' : ''}${user.user_name}`, v: user.login_name, l: roles.length > 0 ? 1 : 0 }));
		}
		if (saUsers.length > 0) {
			if (users.length > 0 || roles.length > 0) this.items.push({ k: 'saUsers', v: `SA ${msg('users')}`, ch: false, l: 0, icon: 'user-crown' });
			saUsers.forEach(user => this.items.push({ k: `${this.includeRoles ? 'u' : ''}${user.user_name}`, v: user.login_name, l: users.length > 0 || roles.length > 0 ? 1 : 0 }));
		}

		return this.items;
	}

	getOptions(): Partial<TreeOptions> {
		return this.options || {};
	}

}

export class GroupUsersTreeAdapter extends TreeAdapter {

	private items: NodeSource[];

	constructor(
		private g: number = null,
		private options?: Partial<TreeOptions>
	) { super() }

	async getTree(): Promise<TreeSource> {
		if (this.items) return this.items;
		const users = await rp.ms('users').get<User[]>(this.g ? `allUsers/${this.g}` : 'groupUsers');
		this.items = users.map(user => ({ k: user.user_name, v: user.login_name, icon: 'user' }));
		return this.items;
	}

	getOptions(): Partial<TreeOptions> {
		return this.options || {};
	}

}

/** Super-admins (users/saUsers) for toolbar dropdowns, optionally preceded by an "all users" entry. */
export class SaUsersTreeAdapter extends TreeAdapter {

	static readonly ALL = 'all';

	private items: NodeSource[];

	constructor(
		private includeAll = false,
		private options?: Partial<TreeOptions>
	) { super() }

	async getTree(): Promise<TreeSource> {
		if (this.items) return this.items;
		const users = await rp.ms('users').get<User[]>('saUsers', { order: [['LOGIN_NAME', 'asc']] });
		this.items = users.map(user => ({ k: user.user_name, v: user.login_name, icon: 'user' }));
		if (this.includeAll) this.items.unshift({ k: SaUsersTreeAdapter.ALL, v: msg('All users'), icon: 'users' });
		return this.items;
	}

	getOptions(): Partial<TreeOptions> {
		return this.options || {};
	}

}
